import React, { useEffect, useRef, Children, createElement } from 'react';
import { Button } from '../ui/Button';
import { motion, useScroll, useTransform } from 'framer-motion';
import { AnimatedGradientBackground } from '../ui/AnimatedGradientBackground';
export function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });
  // Parallax transforms for content (medium speed)
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const videoY = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const statsY = useTransform(scrollYProgress, [0, 1], [0, 20]);
  // Parallax for decorative foreground elements (faster)
  const decorY1 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const decorY2 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const decorRotate = useTransform(scrollYProgress, [0, 1], [0, 15]);
  const decorOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  useEffect(() => {
    // Load Wistia player script
    const script = document.createElement('script');
    script.src = 'https://fast.wistia.com/player.js';
    script.async = true;
    document.head.appendChild(script);
    // Load Wistia embed script
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/60et0u3zqo.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
    return () => {
      // Cleanup scripts on unmount
      if (script.parentNode) document.head.removeChild(script);
      if (embedScript.parentNode) document.head.removeChild(embedScript);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <div ref={heroRef} data-id="element-405">
      <AnimatedGradientBackground className="pt-32 pb-20 md:pt-40 md:pb-28 relative" data-id="element-406">
        {/* Decorative Foreground Elements with Parallax */}
        <motion.div className="absolute top-[15%] right-[10%] w-20 h-20 border border-mint/20 rounded-lg hidden md:block" style={{
        y: decorY1,
        rotate: decorRotate,
        opacity: decorOpacity
      }} data-id="element-407" />
        <motion.div className="absolute top-[25%] left-[8%] w-3 h-3 bg-mint/30 rounded-full hidden md:block" style={{
        y: decorY2,
        opacity: decorOpacity
      }} data-id="element-408" />
        <motion.div className="absolute bottom-[30%] right-[15%] w-2 h-2 bg-white/20 rounded-full hidden md:block" style={{
        y: decorY1,
        opacity: decorOpacity
      }} data-id="element-409" />
        <motion.div className="absolute top-[40%] left-[5%] w-16 h-[1px] bg-gradient-to-r from-transparent via-mint/30 to-transparent hidden md:block" style={{
        y: decorY2,
        opacity: decorOpacity
      }} data-id="element-410" />
        <motion.div className="absolute bottom-[25%] left-[12%] w-12 h-12 border border-white/10 rounded-full hidden md:block" style={{
        y: decorY1,
        rotate: decorRotate,
        opacity: decorOpacity
      }} data-id="element-411" />

        <div className="container-custom relative z-10" data-id="element-412">
          {/* Centered Single Column Layout */}
          <motion.div className="max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-413">
            {/* Headline - Full Width with Parallax */}
            <motion.div style={{
            y: contentY
          }} data-id="element-414">
              <motion.h1 className="mb-6 leading-tight text-center text-white" variants={itemVariants} data-id="element-415">
                Stop Chasing Applicants. Get a Recruiting System.
              </motion.h1>

              {/* Subtitle - Full Width */}
              <motion.p className="text-xl md:text-2xl mb-12 leading-relaxed text-center max-w-3xl mx-auto text-gray-300" variants={itemVariants} data-id="element-416">
                We build the system that delivers{' '}
                <span className="font-bold text-mint" data-id="element-417">
                  20 – 50 qualified interviews every month
                </span>
                . No guesswork. No chaos. Just consistent hires.
              </motion.p>
            </motion.div>

            {/* Video - Centered with Parallax */}
            <motion.div style={{
            y: videoY
          }} data-id="element-418">
              <motion.div className="mb-12" variants={itemVariants} data-id="element-419">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 md:p-6 shadow-2xl max-w-3xl mx-auto border border-white/10" data-id="element-420">
                  <div className="aspect-video bg-deep-blue-light rounded-lg overflow-hidden" data-id="element-421">
                    <style data-id="element-422">{`
                      wistia-player[media-id='60et0u3zqo']:not(:defined) { 
                        background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/60et0u3zqo/swatch'); 
                        display: block; 
                        filter: blur(5px); 
                        padding-top: 56.25%; 
                      }
                    `}</style>
                    <wistia-player media-id="60et0u3zqo" aspect="1.7777777777777777" className="w-full h-full" data-id="element-423"></wistia-player>
                  </div>
                </div>
              </motion.div>

              {/* CTA Button - Centered */}
              <motion.div className="mb-12 flex justify-center" variants={itemVariants} data-id="element-424">
                <Button size="lg" href="/book-call" showArrow data-id="element-425">
                  Start Your Recruiting Engine
                </Button>
              </motion.div>
            </motion.div>

            {/* Trust Stats - Centered with Parallax */}
            <motion.div style={{
            y: statsY
          }} data-id="element-426">
              <motion.div className="flex items-center justify-center gap-8 md:gap-12" variants={itemVariants} data-id="element-427">
                <div className="text-center" data-id="element-428">
                  <div className="text-2xl md:text-3xl font-bold text-mint mb-1" data-id="element-429">
                    4,000+
                  </div>
                  <p className="text-sm text-gray-400" data-id="element-430">System Hires</p>
                </div>
                <div className="text-center" data-id="element-431">
                  <div className="text-2xl md:text-3xl font-bold text-mint mb-1" data-id="element-432">
                    $150M+
                  </div>
                  <p className="text-sm text-gray-400" data-id="element-433">
                    Production from Hired Reps
                  </p>
                </div>
                <div className="text-center" data-id="element-434">
                  <div className="text-2xl md:text-3xl font-bold text-mint mb-1" data-id="element-435">
                    Built by
                  </div>
                  <p className="text-sm text-gray-400" data-id="element-436">D2D Operators</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedGradientBackground>
    </div>;
}